import type {
  CatalogOperatorDownloadInput,
  OperatorDownloadCallbacks,
  OperatorDownloadResult,
} from "./catalogTypes";
import { defaultIgnorableSasFetchError } from "./ignorableSasError";

function jsonDownloadSteps(
  input: CatalogOperatorDownloadInput,
  orderStakeholderKeys: (keys: string[]) => string[],
): number {
  const keysUnordered =
    Object.keys(input.stakeholderSasByCode).length > 0 ? Object.keys(input.stakeholderSasByCode) : [];
  const keys = keysUnordered.length > 0 ? orderStakeholderKeys(keysUnordered) : [];
  return keys.length > 0 ? keys.length + 1 : 1;
}

/** Total progress steps for UI (`done/total`) — must match {@link executeOperatorDownload}. */
export function getOperatorDownloadTotalSteps(
  input: CatalogOperatorDownloadInput,
  orderStakeholderKeys: (keys: string[]) => string[],
): number {
  return jsonDownloadSteps(input, orderStakeholderKeys) + input.manifestFiles.length;
}

function sampleManifestFileFailure(error: unknown): {
  message: string;
  domExceptionName?: string;
} {
  const message = error instanceof Error ? error.message : String(error);
  const cause =
    error instanceof Error && "cause" in error ? (error as Error & { cause?: unknown }).cause : undefined;
  const named =
    (typeof error === "object" &&
      error !== null &&
      "name" in error &&
      typeof (error as { name: unknown }).name === "string" &&
      (error as { name: string }).name) ||
    (typeof cause === "object" &&
      cause !== null &&
      "name" in cause &&
      typeof (cause as { name: unknown }).name === "string" &&
      (cause as { name: string }).name) ||
    undefined;
  const domExceptionName =
    named && named !== "Error" ? named : undefined;
  return {
    message,
    ...(domExceptionName ? { domExceptionName } : {}),
  };
}

/**
 * Cache operator JSON and catalog manifest file blobs (mobile download parity).
 *
 * Order: stakeholder JSON blobs (if any) → base payload → each `manifestFiles` attachment.
 * Progress: `onProgress(done, total)` after each completed step.
 *
 * Manifest file fetch/cache failures are best-effort: core JSON success is enough for a
 * successful operator download (attachments can be fetched on demand when online).
 * Call sites should report a single summary when {@link OperatorDownloadResult.manifestFileFailureCount} > 0.
 */
export async function executeOperatorDownload(
  input: CatalogOperatorDownloadInput,
  callbacks: OperatorDownloadCallbacks,
): Promise<OperatorDownloadResult> {
  const { operatorId: id, name, currentVersion: version, mainSasUrl } = input;
  const stakeholderMap = input.stakeholderSasByCode;

  const keysUnordered =
    stakeholderMap && Object.keys(stakeholderMap).length > 0 ? Object.keys(stakeholderMap) : [];
  const keys =
    keysUnordered.length > 0 ? callbacks.orderStakeholderKeys(keysUnordered) : [];

  const totalSteps = jsonDownloadSteps(input, callbacks.orderStakeholderKeys) + input.manifestFiles.length;
  let done = 0;
  const bump = () => {
    done = Math.min(totalSteps, done + 1);
    callbacks.onProgress?.(done, totalSteps);
  };

  const ignorable = callbacks.isIgnorableSasError ?? defaultIgnorableSasFetchError;

  const mergedStakeholderPayloads: OperatorDownloadResult["stakeholderPayloads"] = {};
  let firstPayload: OperatorDownloadResult["basePayload"] | null = null;

  if (keys.length > 0) {
    for (const code of keys) {
      const sas = stakeholderMap[code];
      if (sas) {
        try {
          const json = await callbacks.fetchOperatorJson(sas);
          const payload = callbacks.normalizePayload(json);
          mergedStakeholderPayloads[code] = payload;
          await callbacks.putStakeholder(id, code, version, payload);
          if (!firstPayload) firstPayload = payload;
        } catch (e) {
          if (!ignorable(e)) throw e;
        }
      }
      bump();
    }
  }

  if (!firstPayload) {
    if (!mainSasUrl) {
      throw new Error(`No SAS URL for operator ${name} (${id})`);
    }
    const json = await callbacks.fetchOperatorJson(mainSasUrl);
    firstPayload = callbacks.normalizePayload(json);
    await callbacks.putBase(id, version, firstPayload);
    bump();
  } else {
    await callbacks.putBase(id, version, firstPayload);
    bump();
  }

  let manifestFileFailureCount = 0;
  let manifestFileFailureSampleMessage: string | undefined;
  let manifestFileFailureSampleDomExceptionName: string | undefined;

  const keepFileIds = new Set<string>();
  for (const file of input.manifestFiles) {
    keepFileIds.add(file.id);
    try {
      if (callbacks.hasCachedFile && (await callbacks.hasCachedFile(id, version, file.id))) {
        bump();
        continue;
      }
      const blob = await callbacks.fetchFileBlob(file.sas);
      await callbacks.putFile(id, version, file, blob);
    } catch (e) {
      // Non-essential: payload already cached; file can be fetched later via SAS when online.
      // Per-file reporters should stay quiet (ignored); call site emits one summary from counts.
      manifestFileFailureCount += 1;
      if (!manifestFileFailureSampleMessage) {
        const sample = sampleManifestFileFailure(e);
        manifestFileFailureSampleMessage = sample.message;
        manifestFileFailureSampleDomExceptionName = sample.domExceptionName;
      }
    }
    bump();
  }

  if (callbacks.pruneFilesNotInManifest) {
    try {
      await callbacks.pruneFilesNotInManifest(id, keepFileIds);
    } catch {
      // Best-effort cleanup; do not fail the download after a successful payload cache.
    }
  }

  return {
    basePayload: firstPayload,
    stakeholderPayloads: mergedStakeholderPayloads,
    manifestFileFailureCount,
    ...(manifestFileFailureSampleMessage
      ? { manifestFileFailureSampleMessage }
      : {}),
    ...(manifestFileFailureSampleDomExceptionName
      ? { manifestFileFailureSampleDomExceptionName }
      : {}),
  };
}
